/**
 * What a video costs the customer.
 *
 * Separate from `cost.ts`, which answers a different question: that one is what
 * the providers charged *us*, recorded after each call and labelled an estimate
 * wherever it is shown. This is the price, and a price has to be a number
 * somebody can be held to.
 *
 * The two are related by one multiplier and one floor, both configurable,
 * because the right margin is a commercial decision and not a fact about the
 * code. Everything else here exists to make the published pricing true: three
 * free videos, five minutes of footage each, and a figure quoted before
 * anything is processed.
 *
 * Read from the environment the way `limits.ts` does, and for the same reason —
 * `core` is bundled into the browser, where `process` does not exist and every
 * value falls back to its default. In the browser these are advisory: the
 * numbers shown on a screen. The server is what charges.
 */
function readEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  return process.env[name];
}

function numberFromEnv(name: string, fallback: number): number {
  const raw = readEnv(name);
  if (raw === undefined || raw.trim() === "") return fallback;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function intFromEnv(name: string, fallback: number): number {
  const raw = readEnv(name);
  if (raw === undefined || raw.trim() === "") return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export const pricing = {
  /**
   * Provider cost multiplied by this to reach the price. 2.2 is cost plus 120%.
   *
   * It has to cover more than the margin suggests. Rendering is not metered at
   * all — `UsageEvent` records provider API calls and nothing else, so the
   * performance machines that actually encode the video are invisible to the
   * number this multiplies. It also has to absorb two known undercounts: usage
   * rows are written fire-and-forget and a failed write bills nothing, and
   * vision calls that report no tokens are priced at zero.
   */
  get marginMultiplier() {
    return numberFromEnv("CREDIT_MARGIN_MULTIPLIER", 2.2);
  },

  /**
   * What a minute of finished video costs to render on our machines, at
   * 1080p30.
   *
   * A separate number from the margin because it is a separate cost, and one
   * the margin never covered: `UsageEvent` records provider API calls, so the
   * performance machines that actually encode the video are invisible to it.
   * A 95-second video took 82 minutes on a `performance-4x`, which is well
   * over an hour of a four-core machine for a minute and a half of output.
   *
   * Nothing is charged for a render that happens in the customer's own
   * browser, because nothing is spent on one. That is the whole of the rule,
   * and it points people at the path that is also faster for them.
   */
  get serverRenderUsdPerMinute() {
    return numberFromEnv("SERVER_RENDER_USD_PER_MINUTE", 0.2);
  },

  /**
   * The least an export can cost.
   *
   * A one-minute demo can run to a few cents of provider spend, and a few cents
   * times any multiplier is still a few cents — while Stripe takes about 45c of
   * a $5 top-up before we see it. Without a floor the cheap videos are sold at
   * a loss that the expensive ones have to carry.
   */
  get minimumChargeUsd() {
    return numberFromEnv("CREDIT_MIN_CHARGE_USD", 0.5);
  },

  /** How many videos are on us. Said in four places on the marketing site. */
  get freeVideoLimit() {
    return intFromEnv("FREE_VIDEO_LIMIT", 3);
  },

  /**
   * The longest recording a free video may be made from.
   *
   * Footage, not finished video: the pricing page says "up to 5 minutes of
   * footage each", and footage is what costs money to transcribe and analyse.
   * A ten-minute take cut down to ninety seconds is still ten minutes of
   * transcription.
   */
  get freeVideoMaxSourceSeconds() {
    return intFromEnv("FREE_VIDEO_MAX_SECONDS", 300);
  },

  /** The smallest top-up. The pricing page says "from $5". */
  get minimumTopUpUsd() {
    return numberFromEnv("MIN_TOPUP_USD", 5);
  },

  /**
   * The largest, which exists to catch a typo rather than to limit anybody.
   * Somebody meaning $50 and typing $5000 should be asked again, not charged.
   */
  get maximumTopUpUsd() {
    return numberFromEnv("MAX_TOPUP_USD", 500);
  },
};

/** The amounts offered as buttons. Anything else is the custom field. */
export const topUpPresetsUsd = [5, 10, 25] as const;

/** The most free videos a team's attribute may give: beyond this it is a typo, and it would not fit the column. */
export const MAX_ORG_FREE_VIDEOS = 1_000_000;

/**
 * A team's `free_videos`, from its PropelAuth org attributes.
 *
 * Set by hand in PropelAuth's dashboard, so it is read forgivingly — `5` and
 * `"5"` both count — and anything else is treated as not set rather than
 * guessed at: a team on the deployment's number is a smaller mistake than a
 * team on a number nobody meant.
 */
export function freeVideosFromMetadata(metadata: unknown): number | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const value = (metadata as Record<string, unknown>).free_videos;
  const number =
    typeof value === "number" ? value : typeof value === "string" && /^\s*\d+\s*$/.test(value) ? Number(value) : NaN;
  return Number.isInteger(number) && number >= 0 && number <= MAX_ORG_FREE_VIDEOS ? number : null;
}

/**
 * A team's `free_forever`, from its PropelAuth org attributes: a team we never
 * charge. Only `true` (or the text "true") turns it on — anything else is off,
 * because a team given everything free by a typo is the costlier mistake.
 */
export function freeForeverFromMetadata(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return false;
  const value = (metadata as Record<string, unknown>).free_forever;
  return value === true || (typeof value === "string" && value.trim().toLowerCase() === "true");
}

/** How many free videos a team gets in all: its own number when it has one, else the deployment's. */
export function freeVideoLimitFor(orgLimit: number | null): number {
  return orgLimit ?? pricing.freeVideoLimit;
}

/**
 * Money, rounded the way money is.
 *
 * Two decimal places, and *up*. Rounding a price down gives away a fraction of
 * a cent on every video, which is trivial; rounding it down having quoted it
 * rounded up is a customer being charged more than they were told, which is
 * not. Up in both places means the quote and the charge agree.
 */
export function roundUsd(amount: number): number {
  // The `toFixed` is not decoration. `1 * 2.2` is 2.2000000000000002 in binary
  // floating point, and ceiling that in cents gives 2.21 — every price a whole
  // cent high, from a multiplication nobody would look at twice. Trimming the
  // noise to six places first leaves a real fraction of a cent intact (1.001
  // still rounds to 1.01) while removing the epsilon that is not one.
  const cents = Number((Math.max(0, amount) * 100).toFixed(6));
  return Math.ceil(cents) / 100;
}

/**
 * What one export costs, from what its providers cost us.
 *
 * Deterministic and pure, so the quote shown before an export and the amount
 * taken after it are the same arithmetic on the same input — which is the only
 * way "the cost is shown before anything is processed" can be true rather than
 * approximately true.
 */
export function priceForExport(providerCostUsd: number): number {
  const cost = Number.isFinite(providerCostUsd) ? Math.max(0, providerCostUsd) : 0;
  return roundUsd(Math.max(cost * pricing.marginMultiplier, pricing.minimumChargeUsd));
}

/**
 * What rendering one video on our own machines costs the customer.
 *
 * Priced on pixels and frames rather than flat, because that is what the
 * machine time actually tracks: a 1080p60 render is twice the frames of
 * 1080p30 and a 720p one is well under half the pixels. Charging them the same
 * would be underwater at one end and daylight robbery at the other.
 *
 * Quotable before the render starts, which matters — "the cost of a video is
 * shown before it is processed" is on the site, and every input here is known
 * from the timeline.
 *
 * Zero is a real answer: a render in the browser spends nothing of ours, so it
 * costs nothing. Callers pass `renderedInBrowser` rather than this guessing.
 */
export function serverRenderPrice(input: {
  durationSeconds: number;
  width: number;
  height: number;
  fps: number;
  renderedInBrowser: boolean;
}): number {
  if (input.renderedInBrowser) return 0;

  const seconds = Number.isFinite(input.durationSeconds) ? Math.max(0, input.durationSeconds) : 0;
  if (seconds === 0) return 0;

  // Against 1080p30, which is what the rate is quoted at.
  const baseline = 1920 * 1080 * 30;
  const rate = Math.max(1, input.width) * Math.max(1, input.height) * Math.max(1, input.fps);
  const relative = Number.isFinite(rate / baseline) ? rate / baseline : 1;

  return roundUsd((seconds / 60) * pricing.serverRenderUsdPerMinute * relative);
}

/** Whether a top-up amount is one we will take. */
export function topUpAmountError(amountUsd: number): string | null {
  if (!Number.isFinite(amountUsd)) return "That is not an amount";
  // Stripe works in cents, and a third decimal place is a typo rather than an
  // intention.
  if (Math.round(amountUsd * 100) !== Number((amountUsd * 100).toFixed(4))) {
    return "Amounts are in whole cents";
  }
  if (amountUsd < pricing.minimumTopUpUsd) return `The smallest top-up is $${pricing.minimumTopUpUsd}`;
  if (amountUsd > pricing.maximumTopUpUsd) return `The largest top-up is $${pricing.maximumTopUpUsd}`;
  return null;
}

export interface Entitlement {
  /** Free videos not yet spent. Never negative. */
  freeRemaining: number;
  freeVideoLimit: number;
  balanceUsd: number;
  /** A team set `free_forever`: nothing it makes is charged. */
  freeForever?: boolean;
}

/**
 * Whether an account may start work at all.
 *
 * The cheap question, asked at upload before a single byte is read: is there
 * anything here to pay with. It cannot know what the video will cost — nothing
 * does yet — only whether the account is empty.
 */
export function canStartWork(entitlement: Entitlement): boolean {
  if (entitlement.freeForever) return true;
  return entitlement.freeRemaining > 0 || entitlement.balanceUsd > 0;
}

/**
 * Whether a recording this long may be processed.
 *
 * The free tier is capped at five minutes of footage. Credit is not: somebody
 * who has paid may process a recording of any length the deployment allows.
 * So the cap only bites when the free videos are the only thing paying.
 */
export function canProcessDuration(entitlement: Entitlement, sourceSeconds: number): boolean {
  if (entitlement.freeForever || entitlement.balanceUsd > 0) return true;
  if (entitlement.freeRemaining <= 0) return false;
  return sourceSeconds <= pricing.freeVideoMaxSourceSeconds;
}

export interface ExportQuote {
  providerCostUsd: number;
  priceUsd: number;
  /** True when this export spends one of the free videos rather than credit. */
  willUseFreeVideo: boolean;
  /** True when this project has already been paid for, so the export is free. */
  alreadyPaid: boolean;
  affordable: boolean;
  /** True when the team is free forever, so nothing is charged. */
  freeForever?: boolean;
}

/**
 * What this export will cost, and whether it can go ahead.
 *
 * Three ways an export is allowed, and the order matters. A project already
 * paid for is free however many times it is re-cut and re-rendered — the
 * pricing page promises exactly that. Otherwise a free video covers it. Only
 * then does it come out of the balance.
 */
export function quoteExport(input: {
  providerCostUsd: number;
  entitlement: Entitlement;
  alreadyPaid: boolean;
}): ExportQuote {
  const priceUsd = priceForExport(input.providerCostUsd);

  if (input.alreadyPaid) {
    return {
      providerCostUsd: input.providerCostUsd,
      priceUsd: 0,
      willUseFreeVideo: false,
      alreadyPaid: true,
      affordable: true,
    };
  }

  // A free-forever team pays nothing and keeps its free videos, so turning
  // the flag off later leaves them as they were.
  if (input.entitlement.freeForever) {
    return {
      providerCostUsd: input.providerCostUsd,
      priceUsd: 0,
      willUseFreeVideo: false,
      alreadyPaid: false,
      affordable: true,
      freeForever: true,
    };
  }

  const willUseFreeVideo = input.entitlement.freeRemaining > 0;
  return {
    providerCostUsd: input.providerCostUsd,
    priceUsd: willUseFreeVideo ? 0 : priceUsd,
    willUseFreeVideo,
    alreadyPaid: false,
    affordable: willUseFreeVideo || input.entitlement.balanceUsd >= priceUsd,
  };
}

/**
 * The refusals that are an offer rather than a fault.
 *
 * Being turned away for want of credit is an ordinary answer with an obvious
 * next step. Dressing it as a broken pipeline sends somebody looking for a
 * fault that is not there — and, worse, leaves them without the one action
 * that would fix it.
 *
 * A set rather than a check per screen, because the same three codes are
 * raised from three places: `no_credit` before an upload, `insufficient_credit`
 * before a render, and `needs_credit_for_length` inside the worker once the
 * recording has been measured.
 */
export const CREDIT_CODES = new Set(["no_credit", "insufficient_credit", "needs_credit_for_length"]);

/** Whether a failure is one a top-up would resolve. */
export function isCreditRefusal(code: string | null | undefined): boolean {
  return Boolean(code && CREDIT_CODES.has(code));
}

/**
 * What a project cost the customer, from its rows in the credit ledger.
 *
 * The same rows the statement lists, so a project's card and the statement can
 * never disagree: charges (a video, a server render) are negative, a refund or
 * an adjustment is positive, and a purchase of credit is not spending on any
 * project. Null until the project has been charged at all, which is different
 * from a video that cost nothing because it used a free allowance.
 */
export function projectCharge(entries: readonly { kind: string; amountUsd: number; usedFreeVideo: boolean }[]): {
  chargedUsd: number | null;
  freeVideo: boolean;
} {
  const spending = entries.filter((entry) => entry.kind !== "purchase");
  if (spending.length === 0) return { chargedUsd: null, freeVideo: false };

  const net = -spending.reduce((total, entry) => total + entry.amountUsd, 0);
  return {
    chargedUsd: Math.max(0, Number(net.toFixed(2))),
    freeVideo: spending.some((entry) => entry.usedFreeVideo),
  };
}
